import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Modal, Form, Input, message, Card, Space, Tag, Select, Row, Col,
  Tooltip, Popconfirm, Typography, Empty, Segmented, DatePicker, Switch,
  AutoComplete, Table, Pagination, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, FolderOpenOutlined,
  StarOutlined, StarFilled, SettingOutlined, AppstoreOutlined, BarsOutlined,
  CloudOutlined, ClockCircleOutlined, WarningOutlined, LinkOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../stores/authStore';
import NetdiskModal from './NetdiskModal';

axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { TextArea } = Input;
const { Text } = Typography;

// 备份状态元数据
const BACKUP_META = {
  none: { label: '未备份', color: '#f5222d', bg: '#fff1f0' },
  local: { label: '本地备份', color: '#fa8c16', bg: '#fff7e6' },
  cloud: { label: '云端备份', color: '#1677ff', bg: '#e6f4ff' },
  both: { label: '双重备份', color: '#52c41a', bg: '#f6ffed' },
};
const BACKUP_OPTIONS = Object.entries(BACKUP_META).map(([value, m]) => ({ value, label: m.label }));

const DEVICE_PRESETS = ['本机', '公司电脑', '移动硬盘', 'U盘', 'NAS/群晖', '百度网盘', '阿里云盘', 'iCloud', 'OneDrive'];

// 按扩展名归类，给出图标与颜色
const EXT_MAP = [
  { exts: ['pdf'], icon: '📕', color: '#e74c3c' },
  { exts: ['doc', 'docx', 'rtf', 'wps'], icon: '📘', color: '#2b579a' },
  { exts: ['xls', 'xlsx', 'csv', 'et'], icon: '📗', color: '#217346' },
  { exts: ['ppt', 'pptx'], icon: '📙', color: '#d24726' },
  { exts: ['txt', 'md'], icon: '📄', color: '#8c8c8c' },
  { exts: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'svg'], icon: '🖼️', color: '#eb2f96' },
  { exts: ['mp4', 'mov', 'avi', 'mkv', 'flv', 'wmv'], icon: '🎬', color: '#722ed1' },
  { exts: ['mp3', 'wav', 'flac', 'aac', 'm4a'], icon: '🎵', color: '#13c2c2' },
  { exts: ['zip', 'rar', '7z', 'tar', 'gz'], icon: '🗜️', color: '#faad14' },
  { exts: ['psd', 'ai', 'sketch', 'fig', 'xd'], icon: '🎨', color: '#ff7a45' },
  { exts: ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'go', 'c', 'cpp', 'html', 'css', 'json'], icon: '💻', color: '#2f54eb' },
  { exts: ['exe', 'dmg', 'app', 'apk', 'msi'], icon: '⚙️', color: '#595959' },
];

const getFileMeta = (fileName = '') => {
  const m = String(fileName).match(/\.([a-z0-9]+)$/i);
  const ext = m ? m[1].toLowerCase() : '';
  const found = EXT_MAP.find((e) => e.exts.includes(ext));
  return found ? { ...found, ext } : { icon: '📁', color: '#6366F1', ext };
};

const parseTags = (str) => (str ? String(str).split(',').map((s) => s.trim()).filter(Boolean) : []);

const expiryInfo = (dateStr) => {
  if (!dateStr) return null;
  const d = dayjs(String(dateStr).slice(0, 10));
  if (!d.isValid()) return null;
  const days = d.diff(dayjs().startOf('day'), 'day');
  if (days < 0) return { text: `已过期 ${-days} 天`, color: '#f5222d', urgent: true };
  if (days === 0) return { text: '今天到期', color: '#f5222d', urgent: true };
  if (days <= 30) return { text: `${days} 天后到期`, color: '#fa8c16', urgent: true };
  return { text: `${d.format('YYYY-MM-DD')} 到期`, color: '#8c8c8c', urgent: false };
};

const Files = () => {
  const { initialize } = useAuthStore();
  const [files, setFiles] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [netdiskInfo, setNetdiskInfo] = useState(null);
  const [netdiskOpen, setNetdiskOpen] = useState(false);
  const isPremium = !!netdiskInfo?.isPremium;

  const fetchNetdisk = async () => {
    try {
      const res = await axios.get('/netdisk/overview');
      if (res.data.success) setNetdiskInfo(res.data);
    } catch (_) { /* ignore */ }
  };

  const [filters, setFilters] = useState({ search: '', type: 'all', device: 'all', backup: 'all', starred: false, sort: 'recent' });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 24, total: 0 });
  const [viewMode, setViewMode] = useState('card');

  const [modalVisible, setModalVisible] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/file-locations', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: filters.search,
          type: filters.type,
          device: filters.device,
          backup: filters.backup,
          starred: filters.starred ? 'true' : '',
          sort: filters.sort,
        },
      });
      if (res.data.success) {
        setFiles(res.data.locations || []);
        setPagination((p) => ({ ...p, total: res.data.pagination?.total || 0 }));
      }
    } catch (e) {
      message.error('获取文件列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get('/file-locations/types');
      if (res.data.success) setFileTypes(res.data.types || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get('/file-locations/stats/overview');
      if (res.data.success) setOverview(res.data.overview);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { initialize(); }, [initialize]);
  useEffect(() => { fetchFiles(); }, [fetchFiles]);
  useEffect(() => { fetchTypes(); fetchOverview(); fetchNetdisk(); }, [fetchTypes, fetchOverview]);

  const setFilter = (patch) => {
    setPagination((p) => ({ ...p, current: 1 }));
    setFilters((f) => ({ ...f, ...patch }));
  };

  const deviceOptions = useMemo(() => {
    const fromData = (overview?.byDevice || []).map((d) => d.name).filter((n) => n && n !== '未指定');
    return Array.from(new Set([...fromData, ...DEVICE_PRESETS])).map((v) => ({ value: v }));
  }, [overview]);

  // ---- 增删改 ----
  const openAdd = () => {
    setEditingFile(null);
    form.resetFields();
    form.setFieldsValue({ backup_status: 'none', is_starred: false });
    setModalVisible(true);
  };

  const openEdit = (rec) => {
    setEditingFile(rec);
    form.setFieldsValue({
      file_name: rec.file_name,
      storage_location: rec.storage_location,
      file_type: rec.file_type || undefined,
      device: rec.device || undefined,
      tags: parseTags(rec.tags),
      file_size: rec.file_size || undefined,
      expiry_date: rec.expiry_date ? dayjs(String(rec.expiry_date).slice(0, 10)) : null,
      backup_status: rec.backup_status || 'none',
      cloud_url: rec.cloud_url || undefined,
      remark: rec.remark || undefined,
      is_starred: !!rec.is_starred,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        tags: (v.tags || []).join(','),
        expiry_date: v.expiry_date ? (dayjs.isDayjs(v.expiry_date) ? v.expiry_date.format('YYYY-MM-DD') : v.expiry_date) : null,
        is_starred: !!v.is_starred,
      };
      if (editingFile) {
        await axios.put(`/file-locations/${editingFile.id}`, payload);
        message.success('已更新');
      } else {
        await axios.post('/file-locations', payload);
        message.success('已添加');
      }
      setModalVisible(false);
      fetchFiles();
      fetchOverview();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/file-locations/${id}`);
      message.success('已删除');
      fetchFiles();
      fetchOverview();
    } catch (e) { message.error('删除失败'); }
  };

  const toggleStar = async (rec) => {
    try {
      const res = await axios.put(`/file-locations/${rec.id}/star`);
      setFiles((list) => list.map((f) => (f.id === rec.id ? { ...f, is_starred: res.data.is_starred } : f)));
      fetchOverview();
    } catch (e) { message.error('操作失败'); }
  };

  const copyPath = (location) => {
    if (!location) return;
    const normalized = location.replace(/\\/g, '/');
    navigator.clipboard?.writeText(normalized).then(
      () => message.success('已复制路径，去文件管理器粘贴即可（Win: Ctrl+L / Mac: Cmd+Shift+G）'),
      () => message.info(`路径：${normalized}`)
    );
  };

  // ---- 类型管理 ----
  const submitType = async () => {
    try {
      const v = await typeForm.validateFields();
      if (editingType) {
        await axios.put(`/file-locations/types/${editingType.id}`, v);
        message.success('类型已更新');
      } else {
        await axios.post('/file-locations/types', v);
        message.success('类型已添加');
      }
      typeForm.resetFields();
      setEditingType(null);
      fetchTypes();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存类型失败');
    }
  };

  const deleteType = async (id) => {
    try {
      await axios.delete(`/file-locations/types/${id}`);
      message.success('类型已删除');
      fetchTypes();
    } catch (e) { message.error('删除类型失败'); }
  };

  // ---- 统计卡 ----
  const statCards = [
    { key: 'total', label: '文件记录', value: overview?.total ?? 0, icon: '📁', color: '#6366F1', onClick: () => setFilter({ type: 'all', device: 'all', backup: 'all', starred: false }) },
    { key: 'device', label: '设备/磁盘', value: overview?.device_count ?? 0, icon: '💽', color: '#13c2c2' },
    { key: 'starred', label: '重要收藏', value: overview?.starred ?? 0, icon: '⭐', color: '#faad14', onClick: () => setFilter({ starred: true }) },
    { key: 'nobackup', label: '未备份', value: overview?.not_backed_up ?? 0, icon: '⚠️', color: '#f5222d', danger: (overview?.not_backed_up ?? 0) > 0, onClick: () => setFilter({ backup: 'none' }) },
    { key: 'netdisk', label: '网盘', value: netdiskInfo?.fileCount ?? 0, icon: '☁️', color: '#6366F1', onClick: () => setNetdiskOpen(true) },
    { key: 'expiry', label: '30天内到期', value: overview?.expiring_soon ?? 0, icon: '⏰', color: '#fa8c16', danger: (overview?.expiring_soon ?? 0) > 0, onClick: () => setFilter({ sort: 'expiry' }) },
  ];

  const typeOptions = fileTypes.map((t) => ({ value: t.type_name, label: t.type_name }));

  const renderCard = (rec) => {
    const meta = getFileMeta(rec.file_name);
    const exp = expiryInfo(rec.expiry_date);
    const bk = BACKUP_META[rec.backup_status || 'none'];
    const tags = parseTags(rec.tags);
    return (
      <Col xs={24} sm={12} md={8} lg={6} xl={6} key={rec.id}>
        <div className="file-card">
          <div className="file-card-head">
            <div className="file-icon" style={{ background: `${meta.color}1a`, color: meta.color }}>{meta.icon}</div>
            <div className="file-title-wrap">
              <Tooltip title={rec.file_name}><div className="file-name">{rec.file_name}</div></Tooltip>
              <div className="file-sub">
                {rec.file_type ? <Tag color="blue" style={{ marginInlineEnd: 4 }}>{rec.file_type}</Tag> : null}
                {rec.file_size ? <Text type="secondary" style={{ fontSize: 12 }}>{rec.file_size}</Text> : null}
              </div>
            </div>
            <StarOutlinedToggle rec={rec} onToggle={toggleStar} />
          </div>

          <div className="file-path" onClick={() => copyPath(rec.storage_location)} title="点击复制路径">
            <FolderOpenOutlined style={{ marginInlineEnd: 6, color: '#8c8c8c' }} />
            <span className="file-path-text">{rec.storage_location}</span>
          </div>

          <div className="file-badges">
            {rec.device ? <span className="badge badge-device">💽 {rec.device}</span> : null}
            <span className="badge" style={{ color: bk.color, background: bk.bg }}>{bk.label}</span>
            {exp ? <span className="badge" style={{ color: exp.color, background: exp.urgent ? '#fff1f0' : '#fafafa' }}>⏰ {exp.text}</span> : null}
            {rec.cloud_url ? <a className="badge badge-link" href={rec.cloud_url} target="_blank" rel="noopener noreferrer"><LinkOutlined /> 云端</a> : null}
          </div>

          {tags.length ? (
            <div className="file-tags">
              {tags.map((t) => <Tag key={t} bordered={false} color="geekblue">{t}</Tag>)}
            </div>
          ) : null}

          <div className="file-actions">
            <Button size="small" type="text" icon={<FolderOpenOutlined />} onClick={() => copyPath(rec.storage_location)}>打开</Button>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(rec)}>编辑</Button>
            <Popconfirm title="确认删除这条记录？" onConfirm={() => handleDelete(rec.id)} okText="删除" cancelText="取消">
              <Button size="small" type="text" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </div>
        </div>
      </Col>
    );
  };

  const tableColumns = [
    {
      title: '文件', dataIndex: 'file_name', key: 'file_name',
      render: (name, rec) => {
        const meta = getFileMeta(name);
        return (
          <Space>
            <span style={{ fontSize: 18 }}>{meta.icon}</span>
            <span>
              {rec.is_starred ? <StarFilled style={{ color: '#faad14', marginInlineEnd: 4 }} /> : null}
              <Text strong>{name}</Text>
              {rec.file_size ? <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 6 }}>{rec.file_size}</Text> : null}
            </span>
          </Space>
        );
      },
    },
    { title: '类型', dataIndex: 'file_type', key: 'file_type', width: 90, render: (t) => t ? <Tag color="blue">{t}</Tag> : '-' },
    { title: '设备', dataIndex: 'device', key: 'device', width: 110, render: (d) => d || '-' },
    {
      title: '存储位置', dataIndex: 'storage_location', key: 'storage_location',
      render: (loc) => <a onClick={() => copyPath(loc)}><Text code style={{ fontSize: 12 }}>{loc}</Text></a>,
    },
    {
      title: '备份', dataIndex: 'backup_status', key: 'backup_status', width: 90,
      render: (s) => { const m = BACKUP_META[s || 'none']; return <Tag color={m.color === '#f5222d' ? 'red' : undefined} style={{ color: m.color }}>{m.label}</Tag>; },
    },
    {
      title: '到期', dataIndex: 'expiry_date', key: 'expiry_date', width: 120,
      render: (d) => { const e = expiryInfo(d); return e ? <Text style={{ color: e.color, fontSize: 12 }}>{e.text}</Text> : '-'; },
    },
    {
      title: '操作', key: 'action', width: 150, fixed: 'right',
      render: (_, rec) => (
        <Space size={0}>
          <Button size="small" type="text" icon={rec.is_starred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} onClick={() => toggleStar(rec)} />
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(rec)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(rec.id)} okText="删除" cancelText="取消">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="files-page">
      {/* 顶栏 */}
      <div className="files-header">
        <div>
          <div className="mod-header-title">文件管理</div>
          <div className="mod-header-subtitle">登记文件的存放位置与设备，随时快速找到 · 追踪备份与有效期</div>
        </div>
        <Space>
          <Button icon={<SettingOutlined />} onClick={() => setTypeModalVisible(true)}>类型管理</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加文件</Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="files-stats">
        {statCards.map((s) => (
          <div
            key={s.key}
            className={`fstat-card ${s.onClick ? 'clickable' : ''} ${s.danger ? 'danger' : ''}`}
            onClick={s.onClick}
          >
            <div className="fstat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div>
              <div className="fstat-value" style={{ color: s.danger ? '#f5222d' : '#1f2430' }}>{s.value}</div>
              <div className="fstat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 工具栏 */}
      <Card className="files-toolbar" bodyStyle={{ padding: 12 }}>
        <div className="toolbar-row">
          <Input.Search
            allowClear
            placeholder="搜索文件名 / 路径 / 标签 / 备注"
            defaultValue={filters.search}
            onSearch={(v) => setFilter({ search: v })}
            style={{ maxWidth: 300, flex: '1 1 220px' }}
          />
          <Select value={filters.type} onChange={(v) => setFilter({ type: v })} style={{ width: 130 }}
            options={[{ value: 'all', label: '全部类型' }, ...typeOptions]} />
          <Select value={filters.device} onChange={(v) => setFilter({ device: v })} style={{ width: 140 }}
            options={[{ value: 'all', label: '全部设备' }, ...(overview?.byDevice || []).filter((d) => d.name !== '未指定').map((d) => ({ value: d.name, label: `${d.name} (${d.count})` }))]} />
          <Select value={filters.backup} onChange={(v) => setFilter({ backup: v })} style={{ width: 130 }}
            options={[{ value: 'all', label: '全部备份' }, ...BACKUP_OPTIONS]} />
          <Select value={filters.sort} onChange={(v) => setFilter({ sort: v })} style={{ width: 130 }}
            options={[{ value: 'recent', label: '最近添加' }, { value: 'name', label: '按名称' }, { value: 'expiry', label: '按到期日' }, { value: 'starred', label: '收藏优先' }]} />
          <Button type={filters.starred ? 'primary' : 'default'} icon={filters.starred ? <StarFilled /> : <StarOutlined />}
            onClick={() => setFilter({ starred: !filters.starred })}>收藏</Button>
          <div style={{ marginInlineStart: 'auto' }}>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[{ value: 'card', icon: <AppstoreOutlined /> }, { value: 'table', icon: <BarsOutlined /> }]}
            />
          </div>
        </div>
      </Card>

      {/* 内容 */}
      <Spin spinning={loading}>
        {files.length === 0 ? (
          <Empty description="还没有文件记录，点击「添加文件」登记第一条" style={{ padding: '60px 0' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加文件</Button>
          </Empty>
        ) : viewMode === 'card' ? (
          <Row gutter={[14, 14]} style={{ marginTop: 4 }}>
            {files.map(renderCard)}
          </Row>
        ) : (
          <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden' }}>
            <Table
              rowKey="id"
              columns={tableColumns}
              dataSource={files}
              pagination={false}
              scroll={{ x: 900 }}
              size="middle"
            />
          </Card>
        )}

        {pagination.total > pagination.pageSize && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={false}
              onChange={(page) => setPagination((p) => ({ ...p, current: page }))}
            />
          </div>
        )}
      </Spin>

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingFile ? '编辑文件记录' : '添加文件记录'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="file_name" label="文件名" rules={[{ required: true, message: '请输入文件名' }]}>
            <Input placeholder="如：2025年度合同.pdf" allowClear />
          </Form.Item>
          <Form.Item
            name="storage_location"
            rules={[{ required: true, message: '请输入存储位置' }]}
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                存储位置 / 路径
                {isPremium && (
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => setNetdiskOpen(true)}>☁️ 网盘</Button>
                )}
              </span>
            }
          >
            <TextArea rows={2} placeholder="如：D:/工作/合同/  或  移动硬盘/照片/2025" allowClear />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="device" label="所在设备 / 磁盘">
                <AutoComplete options={deviceOptions} placeholder="本机 / 移动硬盘 / NAS…" allowClear filterOption={(i, o) => (o?.value || '').toLowerCase().includes(i.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="file_type" label="类型">
                <Select allowClear placeholder="选择类型" options={typeOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="backup_status" label="备份状态">
                <Select options={BACKUP_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="file_size" label="大小（选填）">
                <Input placeholder="如：24MB" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="expiry_date" label="有效期 / 到期日（选填）" tooltip="证件、合同等到期前会在提醒里出现">
                <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cloud_url" label="云端链接（选填）">
                <Input placeholder="https://…" allowClear prefix={<CloudOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="tags" label="标签">
            <Select mode="tags" placeholder="回车添加标签，如 合同 / 重要 / 2025" tokenSeparators={[',']} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="补充说明" allowClear />
          </Form.Item>
          <Form.Item name="is_starred" label="标记为重要" valuePropName="checked">
            <Switch checkedChildren="⭐" unCheckedChildren="" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 类型管理弹窗 */}
      <Modal
        title="文件类型管理"
        open={typeModalVisible}
        onCancel={() => { setTypeModalVisible(false); setEditingType(null); typeForm.resetFields(); }}
        footer={null}
        width={480}
      >
        <Form form={typeForm} layout="inline" onFinish={submitType} style={{ marginBottom: 16 }}>
          <Form.Item name="type_name" rules={[{ required: true, message: '请输入类型名' }]}>
            <Input placeholder="类型名称，如 合同/发票/证件" />
          </Form.Item>
          <Form.Item name="description">
            <Input placeholder="描述（选填）" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">{editingType ? '更新' : '添加'}</Button>
            {editingType && <Button style={{ marginInlineStart: 8 }} onClick={() => { setEditingType(null); typeForm.resetFields(); }}>取消</Button>}
          </Form.Item>
        </Form>
        <div>
          {fileTypes.length === 0 ? <Empty description="暂无类型" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <Space wrap>
              {fileTypes.map((t) => (
                <Tag key={t.id} closable onClose={(e) => { e.preventDefault(); deleteType(t.id); }}
                  onClick={() => { setEditingType(t); typeForm.setFieldsValue({ type_name: t.type_name, description: t.description }); }}
                  style={{ cursor: 'pointer', padding: '4px 10px', fontSize: 13 }} color="blue">
                  {t.type_name}
                </Tag>
              ))}
            </Space>
          )}
        </div>
      </Modal>

      <NetdiskModal open={netdiskOpen} onClose={() => setNetdiskOpen(false)} isPremium={isPremium} onChange={fetchNetdisk} />
    </div>
  );
};

// 收藏切换小组件（卡片右上角）
const StarOutlinedToggle = ({ rec, onToggle }) => (
  <Tooltip title={rec.is_starred ? '取消收藏' : '收藏'}>
    <span className="file-star" onClick={() => onToggle(rec)}>
      {rec.is_starred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined style={{ color: '#c0c4cc' }} />}
    </span>
  </Tooltip>
);

export default Files;
